import { supabase } from '../../config/supabase';
import type { DatabaseCoach, DatabaseSchool } from '../../types/database-types';
import type { BrowseSchool, OutreachEntry, SavedSchool } from '../../types/recruitment';
import {
  assertOutreachEntries,
  normalizeAcademicStage,
  normalizeRecruitmentStage,
  readOutreachEntries,
  toSchoolCoach,
  validStage,
} from '../../types/recruitment';

/** Updates the rower's saved program and fails when no row was written (e.g. it was removed elsewhere). */
async function updateSavedProgram(
  userId: string,
  schoolId: string,
  patch: Record<string, unknown>
): Promise<void> {
  const { data, error } = await supabase
    .from('my_schools')
    .update(patch)
    .eq('user_id', userId)
    .eq('school_id', schoolId)
    .select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('Saved program not found');
}

export const recruitmentService = {
  async getAllSchools(): Promise<BrowseSchool[]> {
    const { data: schoolsData, error: schoolsError } = await supabase
      .from('schools')
      .select('*')
      .eq('is_published', true)
      .order('name');

    if (schoolsError) throw schoolsError;

    const typedSchools = (schoolsData as unknown as DatabaseSchool[]) || [];
    if (typedSchools.length === 0) return [];

    const schoolIds = typedSchools.map((school) => school.id);
    const { data: coachesData, error: coachesError } = await supabase
      .from('coaches')
      .select('*')
      .in('school_id', schoolIds);

    if (coachesError) throw coachesError;

    const typedCoaches = (coachesData as unknown as DatabaseCoach[]) || [];

    return typedSchools.map((school) => {
      const { created_at: _created, updated_at: _updated, ...rest } = school;
      return {
        ...rest,
        coaches: typedCoaches.filter((coach) => coach.school_id === school.id).map(toSchoolCoach),
      };
    });
  },

  async getSavedSchoolIds(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('my_schools')
      .select('school_id')
      .eq('user_id', userId);
    if (error) throw error;

    const typed = (data || []) as Array<{ school_id: string }>;
    return typed.map((row) => row.school_id);
  },

  async getSavedSchools(userId: string): Promise<SavedSchool[]> {
    const { data: mySchoolsData, error: mySchoolsError } = await supabase
      .from('my_schools')
      .select(
        'school_id, favorite, notes, date_added, academic_status, recruitment_status, outreach_entries'
      )
      .eq('user_id', userId);

    if (mySchoolsError) throw mySchoolsError;
    if (!mySchoolsData || mySchoolsData.length === 0) return [];

    const typedMySchools = mySchoolsData as Array<{
      school_id: string;
      favorite: boolean;
      notes: string | null;
      date_added: string | null;
      academic_status: number;
      recruitment_status: number;
      outreach_entries: unknown;
    }>;

    const schoolIds = typedMySchools.map((row) => row.school_id);
    const { data: schoolsData, error: schoolsError } = await supabase
      .from('schools')
      .select('*')
      .in('id', schoolIds);
    if (schoolsError) throw schoolsError;

    const { data: coachesData, error: coachesError } = await supabase
      .from('coaches')
      .select('*')
      .in('school_id', schoolIds);
    if (coachesError) throw coachesError;

    const typedSchools = (schoolsData as unknown as DatabaseSchool[]) || [];
    const typedCoaches = (coachesData as unknown as DatabaseCoach[]) || [];

    return typedSchools.map((school) => {
      const saved = typedMySchools.find((row) => row.school_id === school.id);
      const { created_at: _created, updated_at: _updated, ...rest } = school;

      return {
        ...rest,
        coaches: typedCoaches.filter((coach) => coach.school_id === school.id).map(toSchoolCoach),
        notes: saved?.notes || '',
        addedDate: saved?.date_added || undefined,
        favorite: Boolean(saved?.favorite),
        academic_status: normalizeAcademicStage(saved?.academic_status),
        recruitment_status: normalizeRecruitmentStage(saved?.recruitment_status),
        outreach_entries: readOutreachEntries(saved?.outreach_entries),
      };
    });
  },

  async addSchool(userId: string, schoolId: string): Promise<void> {
    const { error } = await supabase.from('my_schools').insert({
      user_id: userId,
      school_id: schoolId,
      favorite: false,
      academic_status: -1,
      recruitment_status: -1,
    });

    if (error) throw error;
  },

  async removeSchool(userId: string, schoolId: string): Promise<void> {
    const { error } = await supabase
      .from('my_schools')
      .delete()
      .eq('user_id', userId)
      .eq('school_id', schoolId);
    if (error) throw error;
  },

  async toggleFavorite(userId: string, schoolId: string, favorite: boolean): Promise<void> {
    await updateSavedProgram(userId, schoolId, { favorite });
  },

  async saveNotes(userId: string, schoolId: string, notes: string): Promise<void> {
    await updateSavedProgram(userId, schoolId, { notes });
  },

  async updateProgress(
    userId: string,
    schoolId: string,
    type: 'academic' | 'recruitment',
    step: number
  ): Promise<void> {
    if (!validStage(type, step)) throw new Error('Invalid progress stage');

    const { data: current } = await supabase
      .from('my_schools')
      .select('id, academic_status, recruitment_status')
      .eq('user_id', userId)
      .eq('school_id', schoolId)
      .single();

    const oldStatus =
      type === 'academic'
        ? (current as { academic_status?: number } | null)?.academic_status
        : (current as { recruitment_status?: number } | null)?.recruitment_status;

    const updateData =
      type === 'academic' ? { academic_status: step } : { recruitment_status: step };

    await updateSavedProgram(userId, schoolId, updateData);

    if (current?.id) {
      await supabase.from('school_progress_history').insert({
        my_school_id: current.id,
        path: type,
        old_status: oldStatus != null ? String(oldStatus) : null,
        new_status: String(step),
        changed_by: userId,
      });
    }
  },

  async saveOutreach(userId: string, schoolId: string, entries: OutreachEntry[]): Promise<void> {
    const outreachEntries = assertOutreachEntries(entries);
    await updateSavedProgram(userId, schoolId, { outreach_entries: outreachEntries });
  },
};
